import React from 'react';
import usePlayback from './usePlayback';

export default function Timeline({
    currentTime,
    duration,
    isPlaying,
    editorMode,
    tactic,
    selectedPlayerIndex,
    readOnly,
    isFullscreen,
    onTimeChange,
    onPlay,
    onPause,
    onDurationChange,
    onActionClick,
}) {
    // rAF play loop — disabled in setup mode
    usePlayback(isPlaying && editorMode !== 'setup', currentTime, duration, onTimeChange, onPause);

    const togglePlay = () => {
        if (isPlaying) { onPause(); }
        else { if (currentTime >= duration) onTimeChange(0); onPlay(); }
    };

    // Keyframe ticks for selected player
    const selectedPlayer = tactic && selectedPlayerIndex != null && selectedPlayerIndex >= 0
        ? tactic.players?.[selectedPlayerIndex]
        : null;
    const keyframeTicks = selectedPlayer
        ? selectedPlayer.keyframes.map(kf => ({ t: kf.t, color: selectedPlayer.color }))
        : [];

    // Action diamonds
    const actionMarkers = tactic?.actions?.map(a => ({
        t:     a.t,
        color: a.type === 'pass' ? '#44ff88' : '#ff4444',
        action: a,
    })) || [];

    const pct = (t) => `${((t / Math.max(duration, 0.01)) * 100).toFixed(2)}%`;

    const isSetup = editorMode === 'setup';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {/* Marker track */}
            {(keyframeTicks.length > 0 || actionMarkers.length > 0) && !isSetup && (
                <div style={{ position: 'relative', height: '16px', background: 'rgba(0,0,0,0.08)', borderRadius: '4px' }}>
                    {/* Keyframe ticks */}
                    {keyframeTicks.map((kf, i) => (
                        <div
                            key={`kf-${i}`}
                            title={`Keyframe @ ${kf.t.toFixed(1)}s`}
                            style={{
                                position:   'absolute',
                                left:       pct(kf.t),
                                top:        '2px',
                                width:      '3px',
                                height:     '12px',
                                background: kf.color,
                                borderRadius: '2px',
                                transform:  'translateX(-50%)',
                                cursor:     'pointer',
                            }}
                            onClick={() => onTimeChange(kf.t)}
                        />
                    ))}
                    {/* Action diamonds */}
                    {actionMarkers.map((m, i) => (
                        <div
                            key={`act-${i}`}
                            title={`${m.action.type === 'pass' ? 'Pase' : 'Tiro'} @ ${m.t.toFixed(1)}s`}
                            onClick={() => onActionClick(m.t)}
                            style={{
                                position:  'absolute',
                                left:      pct(m.t),
                                top:       '50%',
                                transform: 'translate(-50%, -50%) rotate(45deg)',
                                width:     '9px',
                                height:    '9px',
                                background: m.color,
                                cursor:    'pointer',
                                border:    '1px solid rgba(0,0,0,0.3)',
                            }}
                        />
                    ))}
                    {/* Playhead indicator */}
                    <div style={{
                        position:   'absolute',
                        left:       pct(currentTime),
                        top:        0,
                        width:      '2px',
                        height:     '100%',
                        background: '#fff',
                        transform:  'translateX(-50%)',
                        pointerEvents: 'none',
                    }} />
                </div>
            )}

            {/* Controls row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                {isSetup ? (
                    <span style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
                        Ajusta la formación inicial — cambia a Jugada para animar
                    </span>
                ) : (
                    <>
                        <button className="btn btn-sm btn-primary" onClick={togglePlay} style={{ minWidth: '70px' }}>
                            {isPlaying ? 'Pausar' : 'Play'}
                        </button>

                        <input
                            type="range"
                            min={0} max={duration} step={0.05}
                            value={currentTime}
                            onChange={e => { onPause(); onTimeChange(parseFloat(e.target.value)); }}
                            style={{ flex: 1, minWidth: '100px' }}
                        />

                        <span style={{ fontSize: '0.875rem', whiteSpace: 'nowrap', color: isFullscreen ? '#fff' : '#333' }}>
                            {currentTime.toFixed(1)}s / {duration.toFixed(1)}s
                        </span>

                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { onPause(); onTimeChange(0); }}>
                            ↺
                        </button>

                        {!readOnly && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                <label style={{ fontSize: '0.8rem', margin: 0, whiteSpace: 'nowrap' }}>Dur:</label>
                                <input
                                    type="number" min={1} max={120} step={1}
                                    value={duration}
                                    onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) onDurationChange(v); }}
                                    style={{ width: '56px' }}
                                    className="form-control form-control-sm"
                                />
                                <span style={{ fontSize: '0.8rem' }}>s</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
